// "Turn on notifications": the sheet that asks (NotifyAskSheet.tsx, loaded when it first opens) and
// the provider every page can reach through useNotifyAsk():
//  - open(): from a button (Rewards, Account);
//  - maybeAsk(): at a moment the notifications would help (a cash-out requested, a ticket opened).
//    Only once per visit, never within two weeks of "Not now", and never on a device that's set up,
//    blocked, or can't do it.
import {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useMe } from '../lib/session';
import { lazyComponent } from '../lib/lazy';
import { askSnoozed, notifyMode, notifyState } from '../lib/notify';

interface Ctx {
  open: () => void;
  maybeAsk: () => void;
}
const NotifyCtx = createContext<Ctx>({ open: () => {}, maybeAsk: () => {} });
export const useNotifyAsk = () => useContext(NotifyCtx);

const NotifyAskSheet = lazyComponent(() =>
  import('./NotifyAskSheet').then((m) => m.NotifyAskSheet),
);
export const preloadNotifyAsk = NotifyAskSheet.preload;

export function NotifyAskProvider({ children }: { children: ReactNode }) {
  const { data: me } = useMe();
  const [shown, setShown] = useState<'manual' | 'auto' | null>(null);
  const asked = useRef(false);
  const signedIn = !!me;
  const open = useCallback(() => setShown('manual'), []);
  const maybeAsk = useCallback(() => {
    if (!signedIn || asked.current || askSnoozed()) return;
    const mode = notifyMode();
    if (mode !== 'app' && mode !== 'browser') return;
    asked.current = true;
    void notifyState().then((s) => {
      if (s === 'off') setShown('auto');
    });
  }, [signedIn]);
  const value = useMemo(() => ({ open, maybeAsk }), [open, maybeAsk]);
  return (
    <NotifyCtx.Provider value={value}>
      {children}
      {shown && signedIn && (
        <Suspense fallback={null}>
          <NotifyAskSheet auto={shown === 'auto'} onClose={() => setShown(null)} />
        </Suspense>
      )}
    </NotifyCtx.Provider>
  );
}
